import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Button, Text } from '@fragments';
import { IconPencil } from '@tabler/icons-react';
import ChatMessage from '../../../../components/ChatMessage';
import AccountSummary from '../../../../components/AccountSummary';
import useThreadScroll from '../../../../hooks/useThreadScroll';
import AnalyzingTicker from './components/AnalyzingTicker';
import ProfileSummary from './components/ProfileSummary';
import useOnboardingThread from './hooks/useOnboardingThread';
import './styles.scss';

// The onboarding as a chat above the composer: answered questions only, the one
// being asked is in the composer. The analyzing orb is the @ of the input,
// carried over (shared layoutId). The home scrolls it (`scroller`), so the
// scrollbar spans the canvas; the ref is AnimatePresence's (popLayout measures
// the leaving element).
const OnboardingThread = forwardRef(function OnboardingThread({ studio: s, scroller }, ref) {
  const { t, locale, onboarding: o } = s;
  const { items, orbFor, thinking, editable } = useOnboardingThread(s);
  useThreadScroll(items.length, o.record.id, scroller);
  const body = item => {
    if (item.kind === 'account') return <AccountSummary account={o.record.account} t={t} locale={locale} />;
    if (item.kind === 'analyzing') return <AnalyzingTicker t={t} />;
    if (item.kind === 'question') return <Text.p contrast={9}>{item.question.prompt}</Text.p>;
    if (item.kind === 'answer') return <Text.p contrast={item.skipped ? 6 : 9}>{item.label}</Text.p>;
    if (item.kind === 'summary') return <>
      <Text.p contrast={9}>{t('onboardingSummaryTitle')}</Text.p>
      <ProfileSummary rows={item.rows} t={t} />
    </>;
    return null;
  };
  const actions = item => item.kind === 'answer' && editable && o.editing !== item.question.id
    ? <Button variant="text" color="overlay" size="micro" icon={<IconPencil />} preventLoading aria-label={t('onboardingEditAnswer')} tooltip={t('onboardingEditAnswer')} onClick={() => o.edit(item.question.id)} />
    : null;
  return <motion.section ref={ref} className="studio-onboarding-thread" aria-label={t('onboardingThread')}
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
    <div className="studio-onboarding-thread__log" role="log" aria-live="polite" aria-relevant="additions">
      {items.map(item => <ChatMessage key={item.id} message={item} orb={item.id === orbFor} thinking={item.id === orbFor && thinking}
        orbId="studio-onboarding-mark" actions={actions(item)}>{body(item)}</ChatMessage>)}
    </div>
  </motion.section>;
});

export default OnboardingThread;
